/**
 * "Changed by someone else" check for tally rows (2026-09-29, offline Part 1 point 6).
 *
 * Remembers each tally row's last-changed time exactly as the server last sent it, per
 * `${tallyMasterId}|${orderKey}`. Filled on every load and after every successful save (live or
 * replayed from the offline queue). On save it is sent back as `conflictBase`, and the server
 * refuses the save if a DIFFERENT person changed the row after that time. Own file so both the
 * tally service and the sync engine can use it without importing each other.
 *
 * Position clash (POSITION_CLASH_PLAN.md, 2026-09-29): it also remembers which row keys this device
 * knows exist on the server, and the newest server time among the rows it has seen. A NEW row is
 * sent with the nearest known key above and below it (anchorAfter / anchorBefore) and that time
 * (seenUpTo), so the server can tell when someone else added rows at the same place meanwhile.
 */
const rowBase = new Map<string, string>();
const knownKeys = new Map<number, Set<string>>();
const seenUpTo = new Map<number, string>();

const baseKey = (masterId: number, orderKey: string) => `${masterId}|${orderKey}`;

function known(masterId: number): Set<string> {
  let set = knownKeys.get(masterId);
  if (!set) { set = new Set<string>(); knownKeys.set(masterId, set); }
  return set;
}

/** Newest of two server times (same ISO format from the server, so text order = time order). */
function newer(a: string | undefined, b: string | null | undefined): string | undefined {
  if (!b) return a;
  if (!a) return b;
  return b > a ? b : a;
}

/** Records the server's last-changed time for each row ('' when the row has none).
 *  fromLoad: rows LOADED from the server (not a save reply) — only those move "seen up to": a save
 *  reply is stamped with MY save time, which would wrongly count others' earlier, never-loaded rows
 *  as seen and hide a real position clash. */
export function rememberRowBases(masterId: number | null | undefined, rows: any[] | null | undefined, fromLoad = false): void {
  if (masterId == null) return;
  const keys = known(masterId);
  let seen = seenUpTo.get(masterId);
  (rows || []).forEach((r) => {
    const key = r?.id?.orderKey;
    if (!key) return;
    rowBase.set(baseKey(masterId, key), r?.lastModifiedDate ?? '');
    keys.add(key);
    if (fromLoad) seen = newer(newer(seen, r?.createdDate), r?.lastModifiedDate);
  });
  if (seen) seenUpTo.set(masterId, seen);
}

/** Every row key of a tally, as the server listed it (the grids load this list on open). */
export function rememberKnownKeys(masterId: number | null | undefined, keys: string[] | null | undefined): void {
  if (masterId == null) return;
  const set = known(masterId);
  (keys || []).forEach((k) => { if (k) set.add(k); });
}

/** A row key that no longer exists on the server (deleted, or moved to a new place). */
export function forgetRowKeys(masterId: number | null | undefined, keys: string[]): void {
  if (masterId == null) return;
  const set = knownKeys.get(masterId);
  keys.forEach((k) => { set?.delete(k); rowBase.delete(baseKey(masterId, k)); });
}

/** Stamps `conflictBase` on each row: 'new' if never seen on the server, else the time last seen.
 *  New rows also get anchorAfter / anchorBefore / seenUpTo (position-clash check). */
export function stampConflictBases(details: any[] | null | undefined): void {
  const sortedByMaster = new Map<number, string[]>();
  (details || []).forEach((d) => {
    const masterId = d?.id?.tallyMasterId;
    const key = d?.id?.orderKey;
    if (masterId == null || !key) return;
    d.conflictBase = rowBase.get(baseKey(masterId, key)) ?? 'new';
    if (d.conflictBase !== 'new') return;

    let sorted = sortedByMaster.get(masterId);
    if (!sorted) {
      // Plain JS text order — the same order the server uses (order_key is utf8mb4_bin).
      sorted = [...known(masterId)].sort((x, y) => (x < y ? -1 : x > y ? 1 : 0));
      sortedByMaster.set(masterId, sorted);
    }
    const [after, before] = neighbours(sorted, key);
    d.anchorAfter = after;
    d.anchorBefore = before;
    d.seenUpTo = seenUpTo.get(masterId) ?? null;
  });
}

/** The nearest keys below and above `key` in a sorted list ('' = none). */
function neighbours(sorted: string[], key: string): [string, string] {
  let lo = 0;
  let hi = sorted.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sorted[mid] < key) lo = mid + 1; else hi = mid;
  }
  const after = lo > 0 ? sorted[lo - 1] : '';
  let i = lo;
  while (i < sorted.length && sorted[i] === key) i++;
  const before = i < sorted.length ? sorted[i] : '';
  return [after, before];
}
